import React, { useState, useEffect } from "react";
import { X, MessageSquare, Link, Paperclip, Target } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUSES   = ["Backlog","To Do","In Progress","Review","Done"];
const PRIORITIES = ["Critical","High","Medium","Low"];

const TABS = [
  { key: "details",  label: "Details",   icon: Target        },
  { key: "comments", label: "Comments",  icon: MessageSquare },
  { key: "links",    label: "Links",     icon: Link          },
];

export default function TaskDrawer({ open, task, members = [], projects = [], onClose, onSave, onDelete, onMove }) {
  const isNew = task?._new === true || task?.id === undefined;

  const [tab,       setTab]       = useState("details");
  const [draft,     setDraft]     = useState({});
  const [comments,  setComments]  = useState([]);
  const [comment,   setComment]   = useState("");
  const [goals,     setGoals]     = useState([]);
  const [files,     setFiles]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  useEffect(() => {
    if (task) {
      setDraft({
        title: "", description: "", status: "To Do", priority: "Medium",
        assignee_id: "", project_id: "", due_date: "", goal_id: "",
        perf_score_weight: 1, tags: [],
        ...task,
      });
      setComment(""); setMsg(null); setFiles([]);
    }
  }, [task?.id, task?._new]);

  useEffect(() => {
    if (!open || isNew || !task?.id) return;
    const tok = localStorage.getItem("token");
    const h   = { Authorization: `Bearer ${tok}` };
    Promise.all([
      fetch(`${API}/api/projects/tasks/${task.id}/comments`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/projects/goals`, { headers: h }).then(r => r.json()).catch(() => []),
    ]).then(([c, g]) => {
      setComments(Array.isArray(c) ? c : []);
      setGoals(Array.isArray(g) ? g : []);
    });
  }, [open, task?.id]);

  // Load goals for new tasks too
  useEffect(() => {
    if (!open) return;
    const tok = localStorage.getItem("token");
    fetch(`${API}/api/projects/goals`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.json()).then(g => setGoals(Array.isArray(g) ? g : [])).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!draft.title?.trim()) return;
    setSaving(true); setMsg(null);
    try {
      if (files.length > 0 && !isNew) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        await fetch(`${API}/api/projects/tasks/${draft.id}/attachments`, {
          method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, body: fd,
        });
        setFiles([]);
      }
      await onSave(draft);
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    const tok = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/projects/tasks/${task.id}/comments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: comment }),
      });
      const c = await res.json();
      setComments(prev => [...prev, c]);
      setComment("");
    } catch {}
  };

  const linkedGoal = goals.find(g => g.id === draft.goal_id);
  const assignee   = members.find(m => m.id === draft.assignee_id);

  return (
    <div className="fixed inset-0 z-[10070]" style={{ display: open && task ? "block" : "none" }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[540px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">
        {task && <>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
            <div>
              <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-0.5">{isNew ? "New Task" : draft.project_id ? projects.find(p => p.id === draft.project_id)?.name : "Task"}</p>
              <p className="font-semibold text-white">{isNew ? "Create Task" : draft.title}</p>
            </div>
            <div className="flex items-center gap-2">
              {!isNew && (
                <button onClick={() => onDelete?.(task.id)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400/60 hover:bg-red-500/15 hover:text-red-400 transition-colors">
                  Delete
                </button>
              )}
              <button onClick={onClose}
                className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-4 py-2 border-b border-white/5 shrink-0">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  tab === key ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]" : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
                }`}>
                <Icon size={11} />{label}
              </button>
            ))}
          </div>

          {msg && (
            <div className={`mx-5 mt-3 rounded-lg px-4 py-2 text-xs border ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
              {msg.text}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* ── DETAILS ── */}
            {tab === "details" && (
              <>
                <div>
                  <label className={labelCls}>Title *</label>
                  <input className={inputCls} value={draft.title || ""} onChange={e => set("title", e.target.value)} placeholder="Task title…" />
                </div>

                <div>
                  <label className={labelCls}>Description</label>
                  <textarea rows={3} className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                    value={draft.description || ""} onChange={e => set("description", e.target.value)} placeholder="What needs to be done?" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Project</label>
                    <select className={inputCls} value={draft.project_id || ""} onChange={e => set("project_id", e.target.value)}>
                      <option value="">No project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Assignee</label>
                    <select className={inputCls} value={draft.assignee_id || ""} onChange={e => set("assignee_id", e.target.value)}>
                      <option value="">Unassigned</option>
                      {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Status</label>
                    <select className={inputCls} value={draft.status || "To Do"} onChange={e => set("status", e.target.value)}>
                      {STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Priority</label>
                    <select className={inputCls} value={draft.priority || "Medium"} onChange={e => set("priority", e.target.value)}>
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Due Date</label>
                    <input type="date" className={inputCls} value={draft.due_date || ""} onChange={e => set("due_date", e.target.value)} /></div>
                  <div><label className={labelCls}>Estimated Hours</label>
                    <input type="number" min="0" step="0.5" className={inputCls} value={draft.estimated_hours || ""} onChange={e => set("estimated_hours", parseFloat(e.target.value))} placeholder="0.0" /></div>
                </div>

                {/* Performance score weight */}
                <div className="rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/3 p-4 space-y-3">
                  <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">Performance Integration</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Score Weight (1–5)</label>
                      <input type="number" min="1" max="5" className={inputCls} value={draft.perf_score_weight ?? 1}
                        onChange={e => set("perf_score_weight", Number(e.target.value))} />
                      <p className="text-xs text-white/20 mt-1">Completion contributes this weight to assignee's perf score</p>
                    </div>
                    <div>
                      <label className={labelCls}>Link to Goal / KRA</label>
                      <select className={inputCls} value={draft.goal_id || ""} onChange={e => set("goal_id", e.target.value)}>
                        <option value="">No goal linked</option>
                        {goals.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                      </select>
                    </div>
                  </div>
                  {linkedGoal && (
                    <div className="rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 px-3 py-2">
                      <p className="text-xs text-[#00ff90]/70 font-medium">⚡ {linkedGoal.title}</p>
                      {linkedGoal.description && <p className="text-xs text-white/30 mt-0.5">{linkedGoal.description}</p>}
                    </div>
                  )}
                </div>

                {/* File attach */}
                <label className="flex items-center gap-2 cursor-pointer text-xs text-white/40 hover:text-white/60 transition-colors">
                  <Paperclip size={13} />
                  <span>Attach file(s)</span>
                  <input type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files))} />
                  {files.length > 0 && <span className="text-[#00ff90]/60">{files.length} file(s) selected</span>}
                </label>

                {/* Attachments list */}
                {(draft.attachments || []).length > 0 && (
                  <div className="space-y-1.5">
                    {draft.attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                        <Paperclip size={11} className="text-white/30" />
                        <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-[#00ff90]/60 hover:text-[#00ff90] transition-colors">{a.name}</a>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button onClick={save} disabled={saving || !draft.title?.trim()}
                    className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                    {saving ? "Saving…" : isNew ? "Create Task" : "Save Changes"}
                  </button>
                </div>
              </>
            )}

            {/* ── COMMENTS ── */}
            {tab === "comments" && (
              <div className="space-y-3">
                {isNew ? (
                  <p className="text-white/20 text-sm italic text-center py-6">Save the task first to add comments.</p>
                ) : (
                  <>
                    {comments.length === 0
                      ? <p className="text-white/20 text-sm italic text-center py-4">No comments yet.</p>
                      : comments.map((c, i) => (
                        <div key={i} className="rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white/50">
                              {c.author?.[0]?.toUpperCase()}
                            </div>
                            <span className="text-xs text-white/50 font-medium">{c.author}</span>
                            <span className="text-xs text-white/25">{c.created_at?.split("T")[0]}</span>
                          </div>
                          <p className="text-sm text-white/70">{c.message}</p>
                        </div>
                      ))}
                    <div className="space-y-2">
                      <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)}
                        className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                        placeholder="Add a comment…" />
                      <button onClick={postComment} disabled={!comment.trim()}
                        className="text-xs px-4 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                        Post Comment
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── LINKS ── */}
            {tab === "links" && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Linked Performance Goal</p>
                  {linkedGoal ? (
                    <div className="rounded-xl border border-[#00ff90]/20 bg-[#00ff90]/5 p-4">
                      <p className="text-sm font-medium text-[#00ff90]">{linkedGoal.title}</p>
                      {linkedGoal.description && <p className="text-xs text-white/40 mt-1">{linkedGoal.description}</p>}
                      <p className="text-xs text-white/25 mt-2">Completing this task will contribute toward this goal's progress.</p>
                      <button onClick={() => set("goal_id", "")} className="text-xs text-red-400/60 hover:text-red-400 mt-2 transition-colors">Unlink goal</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-white/30">No goal linked. Linking a task to a performance goal means completion will auto-update the goal's progress.</p>
                      <select className={inputCls} value={draft.goal_id || ""} onChange={e => set("goal_id", e.target.value)}>
                        <option value="">Select a goal to link…</option>
                        {goals.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Performance Score Impact</p>
                  <div className="rounded-xl border border-white/5 bg-white/3 divide-y divide-white/5">
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-white/50">Score Weight</span>
                      <span className="text-sm text-white/70">{draft.perf_score_weight ?? 1} / 5</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-white/50">Assignee</span>
                      <span className="text-sm text-white/70">{assignee?.name || "Unassigned"}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-white/50">Counts toward rating</span>
                      <span className="text-sm text-[#00ff90]/70">When marked Done</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </>}
      </div>
    </div>
  );
}